<main class="workspace" [class.dark-theme]="isDarkTheme">
  @if (workflowMode === 'import') {
    <section class="import-shell">
      <section class="import-card">
        <div class="import-copy">
          <div class="import-copy-header">
            <h2>Import MVR Document</h2>
            <button
              class="theme-toggle import-theme-toggle"
              type="button"
              [class.on]="isDarkTheme"
              (click)="toggleTheme()"
              [attr.aria-label]="isDarkTheme ? 'Switch to light theme' : 'Switch to dark theme'"
            >
              <span></span>
              <b>{{ isDarkTheme ? 'Dark' : 'Light' }}</b>
            </button>
          </div>
        </div>

        <input #pdfInput type="file" accept="application/pdf,.pdf" multiple hidden (change)="onPdfSelected($event)" />

        <button
          class="pdf-dropzone"
          type="button"
          [class.has-file]="selectedPdfFile"
          (click)="pdfInput.click()"
          (drop)="onPdfDrop($event)"
          (dragover)="onDragOver($event)"
        >
          <span class="drop-icon">PDF</span>
          <strong>
            @if (selectedPdfCount > 1) {
              {{ selectedPdfCount }} MVR PDF Documents
            } @else {
              {{ selectedPdfFile ? selectedPdfFile.name : 'MVR PDF Document' }}
            }
          </strong>
          <small>{{ selectedPdfCount ? selectedPdfSize + ' · ' + uploadQueueSummary : 'Drop PDF files here or click to browse' }}</small>
          @if (selectedPdfCount) {
            <i type="button" (click)="clearSelectedPdf($event)">x</i>
          }
        </button>

        @if (uploadQueue.length) {
          <section class="upload-queue-summary" aria-label="Upload queue summary">
            <article>
              <span>Total</span>
              <strong>{{ selectedPdfCount }}</strong>
            </article>
            <article>
              <span>Completed</span>
              <strong>{{ completedUploadCount }}</strong>
            </article>
            <article>
              <span>Pending</span>
              <strong>{{ pendingUploadCount }}</strong>
            </article>
            <article [class.has-error]="failedUploadCount">
              <span>Failed</span>
              <strong>{{ failedUploadCount }}</strong>
            </article>
          </section>

          <section class="upload-file-list" aria-label="Selected upload files">
            @for (item of uploadQueue; track item.id) {
              <article [class.done]="item.status === 'done'" [class.error]="item.status === 'error'" [class.running]="item.status === 'reading' || item.status === 'queued' || item.status === 'running'">
                <span>{{ item.file.name }}</span>
                <small>
                  {{ item.status === 'done' ? 'Extraction complete' : item.status === 'error' ? item.error || 'Failed' : item.status === 'pending' ? 'Pending' : item.message }}
                </small>
              </article>
            }
          </section>
        }

        <section class="upload-status-card" [class.error]="uploadStatus === 'error'" [class.done]="uploadStatus === 'done'">
          <div>
            <span>{{ uploadStepLabel }}</span>
            <strong>{{ uploadMessage }}</strong>
            @if (batchId) {
              <small>Batch ID: {{ batchId }}</small>
            }
            @if (uploadError) {
              <small class="upload-error">{{ uploadError }}</small>
            }
          </div>
          <b>{{ isProcessingUpload ? 'Working' : uploadStatus === 'done' ? 'Done' : 'Ready' }}</b>
        </section>

        <button class="process-button" type="button" [disabled]="!canProcessUpload" (click)="processUploadedPdf()">
          {{ isProcessingUpload ? 'Processing MVR files...' : selectedPdfCount > 1 ? 'Process Files & Review ->' : 'Process & Review ->' }}
        </button>
      </section>

      <section class="driver-history-section" aria-label="Download Driver History">
        <h3>Download Driver History</h3>
        <div class="history-export-controls">
          <div class="history-export-options" role="group" aria-label="Date range">
            <button type="button" [class.active]="driverHistoryExportMode === 'today'" (click)="setDriverHistoryToday()">Today</button>
            <button type="button" [class.active]="driverHistoryExportMode === 'custom'" (click)="setDriverHistoryCustom()">Custom</button>
          </div>

          <div class="history-export-dates">
            <label>
              <span>From</span>
              <input type="date" [(ngModel)]="driverHistoryFromDate" [max]="driverHistoryMaxDate" [disabled]="driverHistoryExportMode === 'today'" />
            </label>
            <label>
              <span>To</span>
              <input type="date" [(ngModel)]="driverHistoryToDate" [max]="driverHistoryMaxDate" [disabled]="driverHistoryExportMode === 'today'" />
            </label>
          </div>

          <button
            class="history-export-download"
            type="button"
            [disabled]="!canDownloadDriverHistory"
            (click)="downloadMvrDriverHistory()"
          >
            {{ driverHistoryExportStatus === 'submitting' ? 'Downloading...' : 'Download Excel' }}
          </button>
        </div>

        @if (driverHistoryExportMessage) {
          <small class="history-export-message" [class.error]="driverHistoryExportStatus === 'error'">
            {{ driverHistoryExportMessage }}
          </small>
        }
      </section>
    </section>
  } @else {
  <section class="shell review-shell">
    <header class="review-header">
      <div class="brand-line">
        <span class="doc-icon"></span>
        <div>
          <h1>MVR Review: {{ pdfFileName }}</h1>
        </div>
      </div>

      <div class="header-status">
        <span class="review-progress-chip">{{ completionPercent }}% Complete</span>
        <span>{{ totalExtractedFieldCount }} Fields Extracted</span>
        <span>{{ totalLowConfidenceFieldCount }} Low Conf</span>
        <span>MVR Provider: {{ mvrProviderLabel }}</span>
        <button
          class="theme-toggle"
          type="button"
          [class.on]="isDarkTheme"
          (click)="toggleTheme()"
          [attr.aria-label]="isDarkTheme ? 'Switch to light theme' : 'Switch to dark theme'"
        >
          <span></span>
          <b>{{ isDarkTheme ? 'Dark' : 'Light' }}</b>
        </button>
        <button type="button" aria-label="Close" (click)="requestCancelReset()">x</button>
      </div>
    </header>

    <section class="progress-rail" aria-label="Review progress">
      <span [style.width.%]="completionPercent"></span>
    </section>

    @if (driverSummaries.length) {
      <section class="driver-dashboard driver-carousel" [ngClass]="driverDashboardColumnClass" aria-label="Driver verification dashboard">
        <div class="driver-ribbon-meta">
          <span class="driver-meta-position">{{ activeDriverPositionLabel }}</span>
          @if (hasMultipleDrivers) {
            <div class="driver-carousel-controls" aria-label="Driver navigation">
              <button type="button" aria-label="Previous driver" (click)="selectAdjacentDriver(-1)">‹</button>
              <select
                aria-label="Select driver"
                [ngModel]="activeDriverIndex"
                (ngModelChange)="selectDriverFromDropdown($event)"
              >
                @for (driver of driverSummaries; track driver.index) {
                  <option [ngValue]="driver.index">Driver {{ driver.index + 1 }} - {{ driver.name }}</option>
                }
              </select>
              <button type="button" aria-label="Next driver" (click)="selectAdjacentDriver(1)">›</button>
            </div>
          }
        </div>
        <div class="driver-ribbon" #driverRibbon>
          @for (driver of driverSummaries; track driver.index) {
            <button
              class="driver-ribbon-card"
              type="button"
              [attr.data-driver-index]="driver.index"
              [attr.aria-hidden]="driver.index === activeDriverIndex ? null : 'true'"
              [class.cell-active]="driver.index === activeDriverIndex"
              (click)="selectDriver(driver.index)"
            >
              <span class="seq-pill" [attr.aria-label]="driver.index + 1">{{ driver.index + 1 }}</span>
              <span class="driver-ribbon-main">
                <span class="driver-position-inline">Driver {{ driver.index + 1 }}/{{ driverSummaries.length }}</span>
                <span class="driver-name-row">
                  <strong>{{ driver.name }}</strong>
                </span>
                <span class="driver-metrics">
                  <b>{{ driver.lowCount }} low</b>
                  <i>{{ driver.extractedCount }} extracted</i>
                </span>
              </span>
            </button>
          }
        </div>
      </section>
    }

    <section class="review-workbench">
      <article class="document-viewer">
        <div class="viewer-toolbar">
          <button type="button" (click)="goToPreviousPdfPage()" [disabled]="currentPdfPage <= 1">&lt;</button>
          <strong>Page {{ currentPdfPage }} / {{ pdfPageCount }}</strong>
          <button type="button" (click)="goToNextPdfPage()" [disabled]="currentPdfPage >= pdfPageCount">&gt;</button>
          <button type="button" (click)="zoomPdfOut()">-</button>
          <strong>{{ pdfZoomPercent }}%</strong>
          <button type="button" (click)="zoomPdfIn()">+</button>
          <button type="button" (click)="fitPdfToWidth()">Fit</button>
          <button
            class="mark-toggle"
            type="button"
            [class.focus-mode]="isDocumentFocusMode"
            (click)="toggleDocumentMarks()"
          >
            {{ documentMarkButtonLabel }}
          </button>
          <button class="driver-template-download" type="button" (click)="downloadDriverExcel()" [disabled]="!driverSummaries.length">
            Download File
          </button>
          @if (bboxEditEnabled) {
            <span class="bbox-edit-status">{{ bboxEditStatusLabel }}</span>
          }

          <span class="toolbar-spacer"></span>
          @if (savedBboxAdjustmentCount) {
            <span class="filter-chip bbox-saved-chip">{{ savedBboxAdjustmentCount }} saved</span>
          }
          <span class="filter-chip low-chip">Low conf</span>
          <span class="filter-chip accepted-chip">Accepted</span>
        </div>

        <div class="pdf-frame" #pdfFrame>
          <div class="pdf-page-stage">
            <canvas #pdfCanvas></canvas>
            <div class="document-hotspot-layer" aria-label="Clickable document highlights">
              @for (field of documentHotspots; track field.id) {
                <button
                  class="document-hotspot"
                  type="button"
                  [class.active]="field.id === selectedFieldId"
                  [class.editable]="canEditBoundingBox(field)"
                  [class.unsaved]="isBboxDraft(field)"
                  [class.saved]="isBboxSaved(field)"
                  [style.left.px]="hotspotLeft(field)"
                  [style.top.px]="hotspotTop(field)"
                  [style.width.px]="hotspotWidth(field)"
                  [style.height.px]="hotspotHeight(field)"
                  (pointerdown)="startBboxMove($event, field)"
                  (click)="onDocumentHotspotClick($event, field)"
                  [attr.aria-label]="'Select ' + field.label"
                >
                  <span>{{ field.label }}</span>
                  @if (canEditBoundingBox(field)) {
                    @for (handle of bboxResizeHandles; track handle.action) {
                      <i
                        [class]="'bbox-resize-handle ' + handle.className"
                        role="presentation"
                        [attr.aria-label]="handle.label"
                        (pointerdown)="startBboxResize($event, field, handle.action)"
                      ></i>
                    }
                  }
                </button>
              }
            </div>
          </div>
        </div>
      </article>

      <aside class="review-sidebar">
        <nav class="review-tabs" aria-label="Review sections">
          <button
            type="button"
            [class.active]="activeTab === 'driver'"
            (click)="activeTab = 'driver'"
          >
            <span class="tab-label">View Driver</span>
            <span class="tab-count" style="visibility: hidden" aria-hidden="true">0</span>
          </button>
          <button
            type="button"
            [class.active]="activeTab === 'violations'"
            (click)="activeTab = 'violations'"
          >
            <span class="tab-label">Driving Record</span>
            <span class="tab-count">{{ drivingRecordCount }}</span>
          </button>
          <button
            type="button"
            [class.active]="activeTab === 'validation'"
            (click)="activeTab = 'validation'"
          >
            <span class="tab-label">Validation</span>
            <span class="tab-count">{{ validationCount }}</span>
          </button>
          <button
            type="button"
            [class.active]="activeTab === 'change-log'"
            (click)="activeTab = 'change-log'"
          >
            <span class="tab-label">Change Log</span>
            <span class="tab-count">{{ changeLogCount }}</span>
          </button>
        </nav>

        <div class="sidebar-body">
        @if (activeTab === 'driver') {
          <section class="field-tools">
            <input type="search" placeholder="Search fields..." />
            <span>Confidence:</span>
            <button class="active" type="button">AI</button>
            <button type="button">Business</button>
          </section>

          <section class="queue-group low-group">
            <button class="queue-title" type="button" (click)="lowGroupOpen = !lowGroupOpen">
              <strong>Needs your review</strong>
              <span>{{ lowFields.length }}</span>
              <i class="chevron-icon" [class.open]="lowGroupOpen"></i>
            </button>

            @if (lowGroupOpen) {
              <div class="verification-table" aria-label="Low confidence driver fields">
                <div class="verification-table-head">
                  <span>Field</span>
                  <span>Extracted Value</span>
                  <span>Confidence</span>
                  <span>Actions</span>
                </div>
                @for (field of lowFields; track field.id) {
                  <article
                    class="verification-row"
                    [class.active]="field.id === selectedFieldId"
                    [class.low]="field.level === 'low'"
                    [class.editing]="field.status === 'editing'"
                    [attr.data-field-id]="field.id"
                  >
                    <button class="verify-field-name" type="button" [attr.title]="field.validationComment ? 'Evidence: ' + field.validationComment : null" (click)="selectField(field)">
                      {{ field.label }}
                      @if (isRequiredReviewField(field)) {
                        <span class="required-star" aria-hidden="true">*</span>
                      }
                    </button>

                    <div class="verify-field-value" (click)="selectField(field)">
                      @if (field.status === 'editing') {
                        @if (isClassADriverExperienceField(field)) {
                          <select #editValueInput class="inline-value-input" [(ngModel)]="field.value" (ngModelChange)="onFieldValueChange(field, $event)" (click)="$event.stopPropagation()">
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        } @else {
                          <input
                            #editValueInput
                            class="inline-value-input"
                            [(ngModel)]="field.value"
                            (ngModelChange)="onFieldValueChange(field, $event)"
                            (click)="$event.stopPropagation()"
                          />
                        }
                      } @else {
                        <strong>{{ displayReviewFieldValue(field) }}</strong>
                      }
                    </div>

                    <button class="verify-confidence" type="button" (click)="selectField(field)">
                      @if (isFieldEdited(field)) {
                        <b class="user-edited">User Edited</b>
                      } @else if (hasExtractedValue(field.value)) {
                        <span>{{ field.confidence }}%</span>
                      }
                    </button>

                    <div class="verify-actions">
                      @if (field.status === 'editing') {
                        @if (isClassADriverExperienceField(field)) {
                          <label class="review-evidence-edit">
                            <span>Evidence</span>
                            <textarea rows="2" [attr.aria-label]="'Evidence for ' + field.label" [(ngModel)]="field.validationComment" (ngModelChange)="onFieldEvidenceChange(field, $event)"></textarea>
                          </label>
                        }
                        @if (!isClassADriverExperienceField(field)) {
                          <select [(ngModel)]="selectedReason">
                            @for (reason of reasons; track reason) {
                              <option [value]="reason">{{ reason }}</option>
                            }
                          </select>
                        }
                        <button class="accept-mini" type="button" [disabled]="!canSaveEditedField(field)" (click)="acceptField(field)">Save</button>
                        <button class="edit-mini" type="button" (click)="cancelEdit(field)">Cancel</button>
                        @if (field.mismatch) {
                          <small class="field-validation-error">Validation: {{ field.mismatch }}</small>
                        }
                      } @else {
                        <button class="icon-action accept-mini" type="button" title="Accept" aria-label="Accept field" [disabled]="submitStatus === 'submitting'" (click)="acceptField(field)">
                          <span aria-hidden="true">✓</span>
                        </button>
                        <button class="icon-action edit-mini" type="button" title="Edit" aria-label="Edit field" [disabled]="submitStatus === 'submitting'" (click)="editField(field)">
                          <span aria-hidden="true">✎</span>
                        </button>
                      }
                    </div>
                  </article>
                }
              </div>
            }

            @if (lowGroupOpen) {
              <div class="queue-list">
                @for (field of lowFields; track field.id) {
                  <article
                    class="queue-item"
                    [class.active]="field.id === selectedFieldId"
                    [attr.data-field-id]="field.id"
                  >
                    <div class="queue-summary review-field-summary" role="button" tabindex="0" [attr.title]="field.validationComment ? 'Evidence: ' + field.validationComment : null" (click)="selectField(field)">
                      <span class="field-title">
                        {{ field.label }}
                        @if (isRequiredReviewField(field)) {
                          <span class="required-star" aria-hidden="true">*</span>
                        }
                      </span>
                      <span class="field-menu">•••</span>
                      @if (field.status === 'editing') {
                        @if (isClassADriverExperienceField(field)) {
                          <select #editValueInput class="inline-value-input" [(ngModel)]="field.value" (ngModelChange)="onFieldValueChange(field, $event)" (click)="$event.stopPropagation()">
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        } @else {
                          <input
                            #editValueInput
                            class="inline-value-input"
                            [(ngModel)]="field.value"
                            (ngModelChange)="onFieldValueChange(field, $event)"
                            (click)="$event.stopPropagation()"
                          />
                        }
                      } @else {
                        <strong class="field-value">{{ displayReviewFieldValue(field) }}</strong>
                      }
                      @if (hasExtractedValue(field.value)) {
                        <span class="confidence-pill">AI {{ field.confidence }}%</span>
                        <span class="confidence-pill biz-pill">BIZ {{ businessConfidence(field) }}%</span>
                      }
                    </div>

                    @if (field.status !== 'editing') {
                      <div class="queue-actions compact-actions">
                        <button class="accept-mini" type="button" [disabled]="submitStatus === 'submitting'" (click)="acceptField(field)">Accept</button>
                        <button class="edit-mini" type="button" [disabled]="submitStatus === 'submitting'" (click)="editField(field)">Edit</button>
                      </div>
                    }

                    @if (field.status === 'editing') {
                      <div class="inline-edit-row">
                        @if (isClassADriverExperienceField(field)) {
                          <label class="review-evidence-edit">
                            <span>Evidence</span>
                            <textarea rows="2" [attr.aria-label]="'Evidence for ' + field.label" [(ngModel)]="field.validationComment" (ngModelChange)="onFieldEvidenceChange(field, $event)"></textarea>
                          </label>
                        }
                        @if (!isClassADriverExperienceField(field)) {
                          <select [(ngModel)]="selectedReason">
                            @for (reason of reasons; track reason) {
                              <option [value]="reason">{{ reason }}</option>
                            }
                          </select>
                        }
                        <button class="accept-mini" type="button" [disabled]="!canSaveEditedField(field)" (click)="acceptField(field)">Save</button>
                        <button class="edit-mini" type="button" (click)="cancelEdit(field)">Cancel</button>
                        @if (field.mismatch) {
                          <small class="field-validation-error">Validation: {{ field.mismatch }}</small>
                        }
                      </div>
                    }
                  </article>
                }
              </div>
            }
          </section>

          <section class="queue-group high-group">
            <button class="queue-title" type="button" (click)="highGroupOpen = !highGroupOpen">
              <strong>High confidence</strong>
              <span>{{ highFields.length }}</span>
              <i class="chevron-icon" [class.open]="highGroupOpen"></i>
            </button>

            @if (highGroupOpen) {
              <div class="verification-table" aria-label="High confidence driver fields">
                <div class="verification-table-head">
                  <span>Field</span>
                  <span>Extracted Value</span>
                  <span>Confidence</span>
                  <span>Actions</span>
                </div>
                @for (field of highFields; track field.id) {
                  <article
                    class="verification-row high-confidence-item"
                    [class.active]="field.id === selectedFieldId"
                    [class.accepted]="field.status === 'accepted'"
                    [class.edited]="isFieldEdited(field)"
                    [class.editing]="field.status === 'editing'"
                    [attr.data-field-id]="field.id"
                  >
                    <button class="verify-field-name" type="button" [attr.title]="field.validationComment ? 'Evidence: ' + field.validationComment : null" (click)="selectField(field)">
                      {{ field.label }}
                      @if (isRequiredReviewField(field)) {
                        <span class="required-star" aria-hidden="true">*</span>
                      }
                    </button>

                    <div class="verify-field-value" (click)="selectField(field)">
                      @if (field.status === 'editing') {
                        @if (isClassADriverExperienceField(field)) {
                          <select #editValueInput class="inline-value-input" [(ngModel)]="field.value" (ngModelChange)="onFieldValueChange(field, $event)" (click)="$event.stopPropagation()">
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        } @else {
                          <input
                            #editValueInput
                            class="inline-value-input"
                            [(ngModel)]="field.value"
                            (ngModelChange)="onFieldValueChange(field, $event)"
                            (click)="$event.stopPropagation()"
                          />
                        }
                      } @else {
                        <strong>{{ displayReviewFieldValue(field) }}</strong>
                      }
                    </div>

                    <button class="verify-confidence" type="button" (click)="selectField(field)">
                      @if (isUserReviewedField(field)) {
                        <b class="user-edited">User Reviewed</b>
                      } @else if (isFieldEdited(field)) {
                        <b class="user-edited">User Edited</b>
                      } @else if (hasExtractedValue(field.value)) {
                        @if (field.level === 'low') {
                          <b>Low</b>
                        }
                        <span [class.high-percent]="field.level === 'high'">{{ field.confidence }}%</span>
                      }
                    </button>

                    <div class="verify-actions">
                      @if (field.status === 'editing') {
                        @if (isClassADriverExperienceField(field)) {
                          <label class="review-evidence-edit">
                            <span>Evidence</span>
                            <textarea rows="2" [attr.aria-label]="'Evidence for ' + field.label" [(ngModel)]="field.validationComment" (ngModelChange)="onFieldEvidenceChange(field, $event)"></textarea>
                          </label>
                        }
                        @if (!isClassADriverExperienceField(field)) {
                          <select [(ngModel)]="selectedReason">
                            @for (reason of reasons; track reason) {
                              <option [value]="reason">{{ reason }}</option>
                            }
                          </select>
                        }
                        <button class="accept-mini" type="button" [disabled]="!canSaveEditedField(field)" (click)="acceptField(field)">Save</button>
                        <button class="edit-mini" type="button" (click)="cancelEdit(field)">Cancel</button>
                        @if (field.mismatch) {
                          <small class="field-validation-error">Validation: {{ field.mismatch }}</small>
                        }
                      } @else {
                        <button class="icon-action accept-mini" type="button" title="Accept" aria-label="Accept field" [disabled]="submitStatus === 'submitting' || field.status === 'accepted'" (click)="acceptField(field)">
                          <span aria-hidden="true">✓</span>
                        </button>
                        <button class="icon-action edit-mini" type="button" title="Edit" aria-label="Edit field" [disabled]="submitStatus === 'submitting'" (click)="editField(field)">
                          <span aria-hidden="true">✎</span>
                        </button>
                      }
                    </div>
                  </article>
                }
              </div>
            }

            @if (highGroupOpen) {
              <div class="queue-list compact-list">
                @for (field of highFields; track field.id) {
                  <article
                    class="queue-item high-confidence-item"
                    [class.active]="field.id === selectedFieldId"
                    [class.accepted]="field.status === 'accepted'"
                    [class.edited]="isFieldEdited(field)"
                  >
                    <button class="queue-summary" type="button" [attr.title]="field.validationComment ? 'Evidence: ' + field.validationComment : null" (click)="selectField(field)">
                      <strong>{{ displayReviewFieldValue(field) }}</strong>
                      <small>{{ field.label }}</small>
                      <span>{{ isUserReviewedField(field) ? 'User Reviewed' : (hasExtractedValue(field.value) ? field.confidence + '%' : '') }}</span>
                      <em>{{ highFieldStatusLabel(field) }}</em>
                    </button>
                    @if (field.status === 'editing') {
                      <div class="inline-edit-row high-confidence-edit">
                        @if (isClassADriverExperienceField(field)) {
                          <label class="review-evidence-edit">
                            <span>Evidence</span>
                            <textarea rows="2" [attr.aria-label]="'Evidence for ' + field.label" [(ngModel)]="field.validationComment" (ngModelChange)="onFieldEvidenceChange(field, $event)"></textarea>
                          </label>
                        }
                        @if (!isClassADriverExperienceField(field)) {
                          <select [(ngModel)]="selectedReason">
                            @for (reason of reasons; track reason) {
                              <option [value]="reason">{{ reason }}</option>
                            }
                          </select>
                        }
                        @if (isClassADriverExperienceField(field)) {
                          <select #editValueInput class="inline-value-input" [(ngModel)]="field.value" (ngModelChange)="onFieldValueChange(field, $event)" (click)="$event.stopPropagation()">
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        } @else {
                          <input
                            #editValueInput
                            class="inline-value-input"
                            [(ngModel)]="field.value"
                            (ngModelChange)="onFieldValueChange(field, $event)"
                            (click)="$event.stopPropagation()"
                          />
                        }
                        <button class="accept-mini" type="button" [disabled]="!canSaveEditedField(field)" (click)="acceptField(field)">Save</button>
                        <button class="edit-mini" type="button" (click)="cancelEdit(field)">Cancel</button>
                        @if (field.mismatch) {
                          <small class="field-validation-error">Validation: {{ field.mismatch }}</small>
                        }
                      </div>
                    } @else {
                      <div class="queue-actions compact-actions">
                        <button class="accept-mini" type="button" [disabled]="submitStatus === 'submitting'" (click)="acceptField(field)">Accept</button>
                        <button class="edit-mini" type="button" [disabled]="submitStatus === 'submitting'" (click)="editField(field)">Edit</button>
                      </div>
                    }
                  </article>
                }
              </div>
            }
          </section>

          @for (section of detailSections; track section.id) {
            <section class="detail-section">
              <button class="detail-title" type="button" (click)="toggleDetailSection(section.id)">
                <strong>{{ section.title }}</strong>
                <span>{{ section.count }}</span>
                <i class="chevron-icon" [class.open]="isDetailSectionOpen(section.id)"></i>
              </button>

              @if (isDetailSectionOpen(section.id)) {
                <div class="detail-list">
                  @if (section.summary) {
                    <article class="section-summary-card">
                      <strong>Summary</strong>
                      <p>{{ section.summary }}</p>
                    </article>
                  }
                  @for (item of section.items; track item.title + item.value + item.meta; let itemIndex = $index) {
                    <article class="record-card">
                      <button class="record-card-header" type="button" (click)="toggleDetailItem(section.id, itemIndex)">
                        @if (item.badge) {
                          <span class="status-badge">{{ item.badge }}</span>
                        }
                        <strong>{{ item.value }}</strong>
                        @if (item.meta) {
                          <small>{{ item.meta }}</small>
                        }
                        <i class="chevron-icon" [class.open]="isDetailItemOpen(section.id, itemIndex)"></i>
                      </button>

                      @if (isDetailItemOpen(section.id, itemIndex) && item.fields?.length) {
                        <div class="record-grid">
                          @for (field of item.fields; track field.label) {
                            <button class="detail-field-jump" type="button" [disabled]="!field.fieldId" [attr.data-field-id]="field.fieldId" [attr.title]="field.validationComment ? 'Evidence: ' + field.validationComment : null" (click)="selectFieldById(field.fieldId)">
                              <span>{{ field.label }}</span>
                              <strong>{{ field.value }}</strong>
                            </button>
                          }
                        </div>
                      }
                    </article>
                  }
                </div>
              }
            </section>
          }
        } @else if (activeTab === 'violations') {
          <section class="violation-panel">
            <article class="section-summary-card">
              <strong>Summary</strong>
              <p>
                {{ violationSummary || violationCount + ' violation record found for ' + activeDriverName + '.' }}
                @if (suspensionSummary) {
                  <br />
                  {{ suspensionSummary }}
                } @else if (suspensionCount) {
                  <br />
                  {{ suspensionCount }} suspension record found for {{ activeDriverName }}.
                }
              </p>
            </article>

            <div class="violation-actions">
              <button class="add-violation-button" type="button" (click)="toggleViolationForm()">
                {{ showViolationForm ? 'Close Add Violation' : 'Add Violation' }}
              </button>
              @if (violationSaveStatus === 'submitting') {
                <span>Saving violation...</span>
              } @else if (violationSaveMessage) {
                <span [class.error]="violationSaveStatus === 'error'">{{ violationSaveMessage }}</span>
              }
            </div>

            @if (showViolationForm) {
              <article class="violation-card violation-form-card">
                <div class="violation-form-grid">
                  <label>
                    <span>Type</span>
                    <button class="field-box-add" type="button" title="Add box" aria-label="Add Type box" (click)="addViolationFieldBox('type', 'Type', 'Type')">+</button>
                    <button class="field-value-save" type="button" title="Save value" aria-label="Save Type value" (click)="saveViolationField('type', 'Type', 'Type')">Save</button>
                    @if (savedViolationFieldValues['type'] !== undefined) {
                      <strong class="saved-violation-value">{{ displayExtractedValue(savedViolationFieldValues['type']) }}</strong>
                    } @else {
                      <input type="text" [(ngModel)]="newViolation.type" (focus)="focusViolationFormField('type', 'Type', 'Type')" />
                    }
                  </label>
                  <label>
                    <span>Violation Date</span>
                    <button class="field-box-add" type="button" title="Add box" aria-label="Add Violation Date box" (click)="addViolationFieldBox('violationDate', 'Violation Date', 'Violation Date')">+</button>
                    <button class="field-value-save" type="button" title="Save value" aria-label="Save Violation Date value" (click)="saveViolationField('violationDate', 'Violation Date', 'Violation Date')">Save</button>
                    @if (savedViolationFieldValues['violationDate'] !== undefined) {
                      <strong class="saved-violation-value">{{ displayExtractedValue(savedViolationFieldValues['violationDate']) }}</strong>
                    } @else {
                      <input type="date" [(ngModel)]="newViolation.violationDate" (focus)="focusViolationFormField('violationDate', 'Violation Date', 'Violation Date')" />
                    }
                  </label>
                  <label>
                    <span>Conviction Date</span>
                    <button class="field-box-add" type="button" title="Add box" aria-label="Add Conviction Date box" (click)="addViolationFieldBox('convictionDate', 'Conviction Date', 'Conviction Date')">+</button>
                    <button class="field-value-save" type="button" title="Save value" aria-label="Save Conviction Date value" (click)="saveViolationField('convictionDate', 'Conviction Date', 'Conviction Date')">Save</button>
                    @if (savedViolationFieldValues['convictionDate'] !== undefined) {
                      <strong class="saved-violation-value">{{ displayExtractedValue(savedViolationFieldValues['convictionDate']) }}</strong>
                    } @else {
                      <input type="date" [(ngModel)]="newViolation.convictionDate" (focus)="focusViolationFormField('convictionDate', 'Conviction Date', 'Conviction Date')" />
                    }
                  </label>
                  <label>
                    <span>ST Code</span>
                    <button class="field-box-add" type="button" title="Add box" aria-label="Add ST Code box" (click)="addViolationFieldBox('stateCode', 'ST Code', 'St Code')">+</button>
                    <button class="field-value-save" type="button" title="Save value" aria-label="Save ST Code value" (click)="saveViolationField('stateCode', 'ST Code', 'St Code')">Save</button>
                    @if (savedViolationFieldValues['stateCode'] !== undefined) {
                      <strong class="saved-violation-value">{{ displayExtractedValue(savedViolationFieldValues['stateCode']) }}</strong>
                    } @else {
                      <input type="text" [(ngModel)]="newViolation.stateCode" (focus)="focusViolationFormField('stateCode', 'ST Code', 'St Code')" />
                    }
                  </label>
                  <label>
                    <span>Location</span>
                    <button class="field-box-add" type="button" title="Add box" aria-label="Add Location box" (click)="addViolationFieldBox('location', 'Location', 'Location')">+</button>
                    <button class="field-value-save" type="button" title="Save value" aria-label="Save Location value" (click)="saveViolationField('location', 'Location', 'Location')">Save</button>
                    @if (savedViolationFieldValues['location'] !== undefined) {
                      <strong class="saved-violation-value">{{ displayExtractedValue(savedViolationFieldValues['location']) }}</strong>
                    } @else {
                      <input type="text" [(ngModel)]="newViolation.location" (focus)="focusViolationFormField('location', 'Location', 'Location')" />
                    }
                  </label>
                  <label>
                    <span>Viol Code</span>
                    <button class="field-box-add" type="button" title="Add box" aria-label="Add Viol Code box" (click)="addViolationFieldBox('violationCode', 'Viol Code', 'Viol Code')">+</button>
                    <button class="field-value-save" type="button" title="Save value" aria-label="Save Viol Code value" (click)="saveViolationField('violationCode', 'Viol Code', 'Viol Code')">Save</button>
                    @if (savedViolationFieldValues['violationCode'] !== undefined) {
                      <strong class="saved-violation-value">{{ displayExtractedValue(savedViolationFieldValues['violationCode']) }}</strong>
                    } @else {
                      <input type="text" [(ngModel)]="newViolation.violationCode" (focus)="focusViolationFormField('violationCode', 'Viol Code', 'Viol Code')" />
                    }
                  </label>
                  <label>
                    <span>Speed</span>
                    <button class="field-box-add" type="button" title="Add box" aria-label="Add Speed box" (click)="addViolationFieldBox('speed', 'Speed', 'Speed')">+</button>
                    <button class="field-value-save" type="button" title="Save value" aria-label="Save Speed value" (click)="saveViolationField('speed', 'Speed', 'Speed')">Save</button>
                    @if (savedViolationFieldValues['speed'] !== undefined) {
                      <strong class="saved-violation-value">{{ displayExtractedValue(savedViolationFieldValues['speed']) }}</strong>
                    } @else {
                      <input type="text" [(ngModel)]="newViolation.speed" (focus)="focusViolationFormField('speed', 'Speed', 'Speed')" />
                    }
                  </label>
                  <label>
                    <span>Points</span>
                    <button class="field-box-add" type="button" title="Add box" aria-label="Add Points box" (click)="addViolationFieldBox('points', 'Points', 'Points')">+</button>
                    <button class="field-value-save" type="button" title="Save value" aria-label="Save Points value" (click)="saveViolationField('points', 'Points', 'Points')">Save</button>
                    @if (savedViolationFieldValues['points'] !== undefined) {
                      <strong class="saved-violation-value">{{ displayExtractedValue(savedViolationFieldValues['points']) }}</strong>
                    } @else {
                      <input type="text" [(ngModel)]="newViolation.points" (focus)="focusViolationFormField('points', 'Points', 'Points')" />
                    }
                  </label>
                  <label class="violation-description-input">
                    <span>Description</span>
                    <button class="field-box-add" type="button" title="Add box" aria-label="Add Description box" (click)="addViolationFieldBox('description', 'Description', 'Description')">+</button>
                    <button class="field-value-save" type="button" title="Save value" aria-label="Save Description value" (click)="saveViolationField('description', 'Description', 'Description')">Save</button>
                    @if (savedViolationFieldValues['description'] !== undefined) {
                      @if (savedViolationDescriptionOptions.length) {
                        <select
                          [ngModel]="savedViolationDescriptionSelection"
                          (ngModelChange)="onSavedViolationDescriptionChange($event)"
                        >
                          @for (option of savedViolationDescriptionOptions; track option.key) {
                            <option [ngValue]="option.value">
                              {{ option.value }}{{ option.confidence !== null ? ' (' + option.confidence + '%)' : '' }}
                            </option>
                          }
                        </select>
                      } @else {
                        <strong class="saved-violation-value">{{ displayExtractedValue(savedViolationFieldValues['description']) }}</strong>
                      }
                    } @else {
                      <textarea rows="3" [(ngModel)]="newViolation.description" (focus)="focusViolationFormField('description', 'Description', 'Description')"></textarea>
                    }
                  </label>
                </div>
                <div class="violation-form-actions">
                  <button class="edit-mini" type="button" (click)="cancelViolationForm()">Cancel</button>
                  <button class="accept-mini" type="button" [disabled]="!canSaveViolation" (click)="addViolation()">
                    {{ violationSaveStatus === 'submitting' ? 'Saving...' : 'Save Violation' }}
                  </button>
                </div>
              </article>
            }

            <header class="record-subsection-title violation-subsection-title">
              <strong>Violations</strong>
              <span>{{ violationCount }}</span>
            </header>

            @for (violation of violationItems; track violation.violationDate + violation.violationCode + violation.description; let violationIndex = $index) {
              <article class="violation-card">
                <button class="violation-card-header" type="button" (click)="toggleViolationItem(violationIndex)">
                  <span class="viol-badge">{{ violation.type }}</span>
                  <strong>{{ violation.violationDate || '—' }}</strong>
                  @if (violation.violationCode) {
                    <b>{{ violation.violationCode }}</b>
                  }
                  @if (violation.points) {
                    <em>{{ violation.points }} pts</em>
                  }
                  <i class="chevron-icon" [class.open]="isViolationItemOpen(violationIndex)"></i>
                </button>

                @if (isViolationItemOpen(violationIndex)) {
                <div class="verification-table violation-record-table" aria-label="Violation fields">
                  <div class="verification-table-head">
                    <span>Field</span>
                    <span>Extracted Value</span>
                    <span>Actions</span>
                  </div>
                  @for (row of violationFieldRows(violation, violationIndex); track row.key) {
                    <article class="verification-row violation-field-row" [class.active]="row.fieldId === selectedFieldId" [class.editing]="!row.isDescription && isEditingViolationField(violationIndex, row.jsonField)" [attr.data-field-id]="row.fieldId">
                      <button class="verify-field-name" type="button" (click)="selectFieldById(row.fieldId, false)">
                        {{ row.label }}
                      </button>

                      <div class="verify-field-value violation-table-value" (click)="selectFieldById(row.fieldId, false)">
                        @if (!row.isDescription && isEditingViolationField(violationIndex, row.jsonField)) {
                          <input
                            class="violation-inline-input"
                            [(ngModel)]="violationFieldEditValues[violationFieldEditKey(violationIndex, row.jsonField)]"
                            (click)="$event.stopPropagation()"
                          />
                        } @else if (row.isDescription && row.descriptionOptions?.length) {
                          <div class="description-picker" (click)="$event.stopPropagation()">
                            <button class="description-picker-trigger" type="button" (click)="toggleDescriptionDropdown(violationIndex, $event)">
                              <span>{{ displayExtractedValue(violation.description) }}</span>
                              <i class="chevron-icon" [class.open]="isDescriptionDropdownOpen(violationIndex)"></i>
                            </button>
                            @if (isDescriptionDropdownOpen(violationIndex)) {
                              <div
                                class="description-picker-backdrop"
                                (click)="closeDescriptionDropdown(violationIndex)"
                                (wheel)="$event.preventDefault()"
                              ></div>
                              <div class="description-picker-menu" [ngStyle]="descriptionDropdownStyle" (wheel)="$event.stopPropagation()">
                                <div class="description-option-list">
                                  @for (option of filteredDescriptionOptions(violationIndex, row.descriptionOptions); track option.key) {
                                    <button
                                      class="description-option"
                                      type="button"
                                      [class.selected]="option.value === violation.description"
                                      (click)="selectViolationDescriptionOption(violationIndex, option)"
                                    >
                                      <strong>{{ descriptionOptionDisplay(option) }}</strong>
                                    </button>
                                  } @empty {
                                    <span class="description-empty">No matching descriptions</span>
                                  }
                                </div>
                                <input
                                  class="description-search-input"
                                  type="search"
                                  placeholder="Search description"
                                  [ngModel]="descriptionSearchTerms[descriptionDropdownKey(violationIndex)] || ''"
                                  (ngModelChange)="onDescriptionSearch(violationIndex, $event)"
                                  (click)="$event.stopPropagation()"
                                />
                              </div>
                            }
                          </div>
                          @if (descriptionSaveStatus[descriptionSelectionKey(violationIndex)] === 'error') {
                            <small [class.error]="descriptionSaveStatus[descriptionSelectionKey(violationIndex)] === 'error'">
                              {{ descriptionSaveMessage[descriptionSelectionKey(violationIndex)] }}
                            </small>
                          }
                        } @else {
                          <strong [title]="row.key === 'originalDescription' ? displayExtractedValue(row.value) : ''">{{ displayExtractedValue(row.value) }}</strong>
                        }
                      </div>

                      @if (!row.isDescription && isEditingViolationField(violationIndex, row.jsonField)) {
                        <div class="violation-edit-panel">
                          <select class="violation-reason-select" [(ngModel)]="selectedReason">
                            @for (reason of reasons; track reason) {
                              <option [value]="reason">{{ reason }}</option>
                            }
                          </select>
                          <div class="violation-edit-actions">
                            <button class="accept-mini" type="button" [disabled]="!canSaveViolationEditedField(violationIndex, row.jsonField)" (click)="$event.stopPropagation(); saveViolationExistingField(violationIndex, row.jsonField)">Save</button>
                            <button class="edit-mini violation-cancel-mini" type="button" [disabled]="violationFieldEditStatus[violationFieldEditKey(violationIndex, row.jsonField)] === 'submitting'" (click)="$event.stopPropagation(); cancelViolationFieldEdit(violationIndex, row.jsonField)">Cancel</button>
                          </div>
                          @if (violationFieldEditStatus[violationFieldEditKey(violationIndex, row.jsonField)] === 'error') {
                            <small class="error">Validation: {{ violationFieldEditMessage[violationFieldEditKey(violationIndex, row.jsonField)] }}</small>
                          }
                        </div>
                      }

                      <div class="verify-actions">
                        @if (row.isDescription && row.descriptionOptions?.length) {
                          <button class="field-value-save description-save" type="button" [disabled]="descriptionSaveStatus[descriptionSelectionKey(violationIndex)] === 'submitting'" (click)="$event.stopPropagation(); saveViolationDescription(violationIndex)">
                            {{ descriptionSaveStatus[descriptionSelectionKey(violationIndex)] === 'submitting' ? 'Saving...' : 'Save' }}
                          </button>
                        }
                        @if (!row.isDescription) {
                          <button class="icon-action edit-mini" type="button" title="Edit" [attr.aria-label]="'Edit ' + row.label" (click)="$event.stopPropagation(); startViolationFieldEdit(violationIndex, row.jsonField, row.value, row.label)">
                          <span aria-hidden="true">âœŽ</span>
                          </button>
                        }
                      </div>
                    </article>
                  }
                </div>
                <div class="record-grid">
                  <div class="detail-field-jump" role="button" tabindex="0" (click)="selectFieldById(violation.fieldIds['type'], false)">
                    <span>Type</span>
                    @if (isEditingViolationField(violationIndex, 'Type')) {
                      <input class="violation-inline-input" [(ngModel)]="violationFieldEditValues[violationFieldEditKey(violationIndex, 'Type')]" (click)="$event.stopPropagation()" />
                      <div class="violation-inline-actions">
                        <button class="field-value-save description-save" type="button" (click)="$event.stopPropagation(); saveViolationExistingField(violationIndex, 'Type')">{{ violationFieldEditStatus[violationFieldEditKey(violationIndex, 'Type')] === 'submitting' ? 'Saving...' : 'Save' }}</button>
                        <button class="edit-mini" type="button" (click)="$event.stopPropagation(); cancelViolationFieldEdit(violationIndex, 'Type')">Cancel</button>
                      </div>
                    } @else {
                      <strong>{{ violation.type || '--' }}</strong>
                      <button class="icon-action edit-mini violation-edit-icon" type="button" title="Edit" aria-label="Edit Type" (click)="$event.stopPropagation(); startViolationFieldEdit(violationIndex, 'Type', violation.type)"><span aria-hidden="true">✎</span></button>
                    }
                  </div>
                  <div class="detail-field-jump" role="button" tabindex="0" (click)="selectFieldById(violation.fieldIds['violationDate'], false)">
                    <span>Violation Date</span>
                    @if (isEditingViolationField(violationIndex, 'Violation Date')) {
                      <input class="violation-inline-input" [(ngModel)]="violationFieldEditValues[violationFieldEditKey(violationIndex, 'Violation Date')]" (click)="$event.stopPropagation()" />
                      <div class="violation-inline-actions">
                        <button class="field-value-save description-save" type="button" (click)="$event.stopPropagation(); saveViolationExistingField(violationIndex, 'Violation Date')">{{ violationFieldEditStatus[violationFieldEditKey(violationIndex, 'Violation Date')] === 'submitting' ? 'Saving...' : 'Save' }}</button>
                        <button class="edit-mini" type="button" (click)="$event.stopPropagation(); cancelViolationFieldEdit(violationIndex, 'Violation Date')">Cancel</button>
                      </div>
                    } @else {
                      <strong>{{ violation.violationDate || '—' }}</strong>
                      <button class="icon-action edit-mini violation-edit-icon" type="button" title="Edit" aria-label="Edit Violation Date" (click)="$event.stopPropagation(); startViolationFieldEdit(violationIndex, 'Violation Date', violation.violationDate, 'Violation Date')"><span aria-hidden="true">✎</span></button>
                    }
                  </div>
                  <div class="detail-field-jump" role="button" tabindex="0" (click)="selectFieldById(violation.fieldIds['convictionDate'], false)">
                    <span>Conviction Date</span>
                    @if (isEditingViolationField(violationIndex, 'Conviction Date')) {
                      <input class="violation-inline-input" [(ngModel)]="violationFieldEditValues[violationFieldEditKey(violationIndex, 'Conviction Date')]" (click)="$event.stopPropagation()" />
                      <div class="violation-inline-actions">
                        <button class="field-value-save description-save" type="button" (click)="$event.stopPropagation(); saveViolationExistingField(violationIndex, 'Conviction Date')">{{ violationFieldEditStatus[violationFieldEditKey(violationIndex, 'Conviction Date')] === 'submitting' ? 'Saving...' : 'Save' }}</button>
                        <button class="edit-mini" type="button" (click)="$event.stopPropagation(); cancelViolationFieldEdit(violationIndex, 'Conviction Date')">Cancel</button>
                      </div>
                    } @else {
                      <strong>{{ violation.convictionDate || '—' }}</strong>
                      <button class="icon-action edit-mini violation-edit-icon" type="button" title="Edit" aria-label="Edit Conviction Date" (click)="$event.stopPropagation(); startViolationFieldEdit(violationIndex, 'Conviction Date', violation.convictionDate, 'Conviction Date')"><span aria-hidden="true">✎</span></button>
                    }
                  </div>
                  <div class="detail-field-jump" role="button" tabindex="0" (click)="selectFieldById(violation.fieldIds['stateCode'], false)">
                    <span>ST Code</span>
                    @if (isEditingViolationField(violationIndex, 'St Code')) {
                      <input class="violation-inline-input" [(ngModel)]="violationFieldEditValues[violationFieldEditKey(violationIndex, 'St Code')]" (click)="$event.stopPropagation()" />
                      <div class="violation-inline-actions">
                        <button class="field-value-save description-save" type="button" (click)="$event.stopPropagation(); saveViolationExistingField(violationIndex, 'St Code')">{{ violationFieldEditStatus[violationFieldEditKey(violationIndex, 'St Code')] === 'submitting' ? 'Saving...' : 'Save' }}</button>
                        <button class="edit-mini" type="button" (click)="$event.stopPropagation(); cancelViolationFieldEdit(violationIndex, 'St Code')">Cancel</button>
                      </div>
                    } @else {
                      <strong>{{ violation.stateCode || '—' }}</strong>
                      <button class="icon-action edit-mini violation-edit-icon" type="button" title="Edit" aria-label="Edit ST Code" (click)="$event.stopPropagation(); startViolationFieldEdit(violationIndex, 'St Code', violation.stateCode, 'ST Code')"><span aria-hidden="true">✎</span></button>
                    }
                  </div>
                  <div class="detail-field-jump" role="button" tabindex="0" (click)="selectFieldById(violation.fieldIds['location'], false)">
                    <span>Location</span>
                    @if (isEditingViolationField(violationIndex, 'Location')) {
                      <input class="violation-inline-input" [(ngModel)]="violationFieldEditValues[violationFieldEditKey(violationIndex, 'Location')]" (click)="$event.stopPropagation()" />
                      <div class="violation-inline-actions">
                        <button class="field-value-save description-save" type="button" (click)="$event.stopPropagation(); saveViolationExistingField(violationIndex, 'Location')">{{ violationFieldEditStatus[violationFieldEditKey(violationIndex, 'Location')] === 'submitting' ? 'Saving...' : 'Save' }}</button>
                        <button class="edit-mini" type="button" (click)="$event.stopPropagation(); cancelViolationFieldEdit(violationIndex, 'Location')">Cancel</button>
                      </div>
                    } @else {
                      <strong>{{ violation.location || '—' }}</strong>
                      <button class="icon-action edit-mini violation-edit-icon" type="button" title="Edit" aria-label="Edit Location" (click)="$event.stopPropagation(); startViolationFieldEdit(violationIndex, 'Location', violation.location, 'Location')"><span aria-hidden="true">✎</span></button>
                    }
                  </div>
                  <div class="detail-field-jump" role="button" tabindex="0" (click)="selectFieldById(violation.fieldIds['violationCode'], false)">
                    <span>Viol Code</span>
                    @if (isEditingViolationField(violationIndex, 'Viol Code')) {
                      <input class="violation-inline-input" [(ngModel)]="violationFieldEditValues[violationFieldEditKey(violationIndex, 'Viol Code')]" (click)="$event.stopPropagation()" />
                      <div class="violation-inline-actions">
                        <button class="field-value-save description-save" type="button" (click)="$event.stopPropagation(); saveViolationExistingField(violationIndex, 'Viol Code')">{{ violationFieldEditStatus[violationFieldEditKey(violationIndex, 'Viol Code')] === 'submitting' ? 'Saving...' : 'Save' }}</button>
                        <button class="edit-mini" type="button" (click)="$event.stopPropagation(); cancelViolationFieldEdit(violationIndex, 'Viol Code')">Cancel</button>
                      </div>
                    } @else {
                      <strong>{{ violation.violationCode || '—' }}</strong>
                      <button class="icon-action edit-mini violation-edit-icon" type="button" title="Edit" aria-label="Edit Viol Code" (click)="$event.stopPropagation(); startViolationFieldEdit(violationIndex, 'Viol Code', violation.violationCode, 'Viol Code')"><span aria-hidden="true">✎</span></button>
                    }
                  </div>
                  <div class="detail-field-jump" role="button" tabindex="0" (click)="selectFieldById(violation.fieldIds['speed'], false)">
                    <span>Speed</span>
                    @if (isEditingViolationField(violationIndex, 'Speed')) {
                      <input class="violation-inline-input" [(ngModel)]="violationFieldEditValues[violationFieldEditKey(violationIndex, 'Speed')]" (click)="$event.stopPropagation()" />
                      <div class="violation-inline-actions">
                        <button class="field-value-save description-save" type="button" (click)="$event.stopPropagation(); saveViolationExistingField(violationIndex, 'Speed')">{{ violationFieldEditStatus[violationFieldEditKey(violationIndex, 'Speed')] === 'submitting' ? 'Saving...' : 'Save' }}</button>
                        <button class="edit-mini" type="button" (click)="$event.stopPropagation(); cancelViolationFieldEdit(violationIndex, 'Speed')">Cancel</button>
                      </div>
                    } @else {
                      <strong>{{ violation.speed || '—' }}</strong>
                      <button class="icon-action edit-mini violation-edit-icon" type="button" title="Edit" aria-label="Edit Speed" (click)="$event.stopPropagation(); startViolationFieldEdit(violationIndex, 'Speed', violation.speed, 'Speed')"><span aria-hidden="true">✎</span></button>
                    }
                  </div>
                  <div class="detail-field-jump" role="button" tabindex="0" (click)="selectFieldById(violation.fieldIds['points'], false)">
                    <span>Points</span>
                    @if (isEditingViolationField(violationIndex, 'Points')) {
                      <input class="violation-inline-input" [(ngModel)]="violationFieldEditValues[violationFieldEditKey(violationIndex, 'Points')]" (click)="$event.stopPropagation()" />
                      <div class="violation-inline-actions">
                        <button class="field-value-save description-save" type="button" (click)="$event.stopPropagation(); saveViolationExistingField(violationIndex, 'Points')">{{ violationFieldEditStatus[violationFieldEditKey(violationIndex, 'Points')] === 'submitting' ? 'Saving...' : 'Save' }}</button>
                        <button class="edit-mini" type="button" (click)="$event.stopPropagation(); cancelViolationFieldEdit(violationIndex, 'Points')">Cancel</button>
                      </div>
                    } @else {
                      <strong>{{ violation.points || '—' }}</strong>
                      <button class="icon-action edit-mini violation-edit-icon" type="button" title="Edit" aria-label="Edit Points" (click)="$event.stopPropagation(); startViolationFieldEdit(violationIndex, 'Points', violation.points, 'Points')"><span aria-hidden="true">✎</span></button>
                    }
                  </div>
                </div>

                <div class="description-box detail-field-jump" role="button" tabindex="0" (click)="selectFieldById(violation.fieldIds['description'], false)">
                  <div class="description-box-header">
                    <span>Description</span>
                    @if (violation.descriptionConfidence !== null) {
                      <em>{{ violation.descriptionConfidence }}% confidence</em>
                    }
                  </div>
                  @if (violation.descriptionOptions.length) {
                    <select
                      [ngModel]="violation.description"
                      (ngModelChange)="onViolationDescriptionChange(violationIndex, $event)"
                      (click)="$event.stopPropagation()"
                    >
                      @for (option of violation.descriptionOptions; track option.key) {
                        <option [ngValue]="option.value">
                          {{ option.value }}{{ option.confidence !== null ? ' (' + option.confidence + '%)' : '' }}
                        </option>
                      }
                    </select>
                    <button class="field-value-save description-save" type="button" (click)="$event.stopPropagation(); saveViolationDescription(violationIndex)">
                      {{ descriptionSaveStatus[descriptionSelectionKey(violationIndex)] === 'submitting' ? 'Saving...' : 'Save' }}
                    </button>
                    @if (descriptionSaveMessage[descriptionSelectionKey(violationIndex)]) {
                      <small [class.error]="descriptionSaveStatus[descriptionSelectionKey(violationIndex)] === 'error'">
                        {{ descriptionSaveMessage[descriptionSelectionKey(violationIndex)] }}
                      </small>
                    }
                  }
                  <strong>{{ violation.description || '—' }}</strong>
                </div>
                }
              </article>
            } @empty {
              <section class="empty-tab">
                <h2>Violations</h2>
                <p>No violation records found for {{ activeDriverName }}.</p>
              </section>
            }

            <section class="driving-record-subsection">
              <header class="record-subsection-title">
                <strong>Suspensions</strong>
                <span>{{ suspensionCount }}</span>
              </header>

              @for (suspension of suspensionItems; track suspension.action + suspension.effectiveDate + suspension.description; let suspensionIndex = $index) {
                <article class="violation-card suspension-card">
                  <button class="violation-card-header" type="button" (click)="toggleSuspensionItem(suspensionIndex)">
                    <span class="viol-badge">{{ suspension.action || 'SUSP' }}</span>
                    <strong>{{ suspension.effectiveDate || suspension.orderDate || 'No date' }}</strong>
                    @if (suspension.code) {
                      <b>{{ suspension.code }}</b>
                    }
                    @if (suspension.endDate) {
                      <em>End {{ suspension.endDate }}</em>
                    }
                    <i class="chevron-icon" [class.open]="isSuspensionItemOpen(suspensionIndex)"></i>
                  </button>

                  @if (isSuspensionItemOpen(suspensionIndex)) {
                    <div class="verification-table violation-record-table suspension-record-table" aria-label="Suspension fields">
                      <div class="verification-table-head">
                        <span>Field</span>
                        <span>Extracted Value</span>
                        <span>Actions</span>
                      </div>
                      @for (row of suspensionFieldRows(suspension, suspensionIndex); track row.key) {
                        <article class="verification-row violation-field-row" [class.active]="row.fieldId === selectedFieldId" [class.editing]="isEditingSuspensionField(suspensionIndex, row.jsonField)" [attr.data-field-id]="row.fieldId">
                          <button class="verify-field-name" type="button" (click)="selectFieldById(row.fieldId, false)">
                            {{ row.label }}
                          </button>

                          <div class="verify-field-value violation-table-value" (click)="selectFieldById(row.fieldId, false)">
                            @if (isEditingSuspensionField(suspensionIndex, row.jsonField)) {
                              <input
                                class="violation-inline-input"
                                [(ngModel)]="suspensionFieldEditValues[suspensionFieldEditKey(suspensionIndex, row.jsonField)]"
                                (click)="$event.stopPropagation()"
                              />
                            } @else {
                              <strong>{{ displayExtractedValue(row.value) }}</strong>
                            }
                          </div>

                          @if (isEditingSuspensionField(suspensionIndex, row.jsonField)) {
                            <div class="violation-edit-panel">
                              <select class="violation-reason-select" [(ngModel)]="selectedReason">
                                @for (reason of reasons; track reason) {
                                  <option [value]="reason">{{ reason }}</option>
                                }
                              </select>
                              <div class="violation-edit-actions">
                                <button class="accept-mini" type="button" [disabled]="!canSaveSuspensionEditedField(suspensionIndex, row.jsonField)" (click)="$event.stopPropagation(); saveSuspensionExistingField(suspensionIndex, row.jsonField)">Save</button>
                                <button class="edit-mini violation-cancel-mini" type="button" [disabled]="suspensionFieldEditStatus[suspensionFieldEditKey(suspensionIndex, row.jsonField)] === 'submitting'" (click)="$event.stopPropagation(); cancelSuspensionFieldEdit(suspensionIndex, row.jsonField)">Cancel</button>
                              </div>
                              @if (suspensionFieldEditStatus[suspensionFieldEditKey(suspensionIndex, row.jsonField)] === 'error') {
                                <small class="error">Validation: {{ suspensionFieldEditMessage[suspensionFieldEditKey(suspensionIndex, row.jsonField)] }}</small>
                              }
                            </div>
                          }

                          <div class="verify-actions">
                            <button class="icon-action edit-mini" type="button" title="Edit" [attr.aria-label]="'Edit ' + row.label" (click)="$event.stopPropagation(); startSuspensionFieldEdit(suspensionIndex, row.jsonField, row.value, row.label)">
                              <span aria-hidden="true">✎</span>
                            </button>
                          </div>
                        </article>
                      }
                    </div>
                  }
                </article>
              } @empty {
                <section class="empty-tab compact-empty">
                  <h2>Suspensions</h2>
                  <p>No suspension records found for {{ activeDriverName }}.</p>
                </section>
              }
            </section>
          </section>
        } @else if (activeTab === 'validation') {
          <section class="validation-panel">
            @for (item of validationItems; track item.id) {
              <article class="validation-card" [class.editing]="isValidationItemEditing(item)">
                <span>Driver {{ item.driverIndex + 1 }} - {{ item.driverName }}</span>
                <strong>{{ item.field }}</strong>
                <p><b>Value:</b> {{ item.value }}</p>
                <p><b>Validation:</b> {{ item.comment }}</p>

                @if (isValidationItemEditing(item)) {
                  <div class="validation-edit-panel">
                    <label>
                      <span>Correct Value</span>
                      <input
                        class="validation-inline-input"
                        [(ngModel)]="validationFieldEditValues[item.id]"
                        (ngModelChange)="onValidationItemValueChange(item, $event)"
                      />
                    </label>
                    <select class="validation-reason-select" [(ngModel)]="selectedReason">
                      @for (reason of reasons; track reason) {
                        <option [value]="reason">{{ reason }}</option>
                      }
                    </select>
                    <div class="queue-actions validation-card-actions">
                      <button class="accept-mini" type="button" [disabled]="!canSaveValidationItem(item)" (click)="saveValidationItemEdit(item)">Save</button>
                      <button class="edit-mini" type="button" [disabled]="submitStatus === 'submitting'" (click)="cancelValidationItemEdit(item)">Cancel</button>
                    </div>
                  </div>
                } @else {
                  <div class="queue-actions validation-card-actions">
                    <button class="accept-mini" type="button" [disabled]="submitStatus === 'submitting' || !canEditValidationItem(item)" (click)="startValidationItemEdit(item)">Fix Here</button>
                  </div>
                }

                @if (validationFieldEditMessage[item.id]) {
                  <small class="validation-edit-message" [class.error]="validationFieldEditStatus[item.id] === 'error'">{{ validationFieldEditMessage[item.id] }}</small>
                }
              </article>
            } @empty {
              <section class="empty-tab">
                <h2>Validation</h2>
                <p>No validation issues returned yet.</p>
              </section>
            }
          </section>
        } @else {
          <section class="change-log-panel">
            @for (entry of changeLogEntries; track entry.id) {
              <article class="change-log-card">
                <span>{{ entry.changeType }}</span>
                <strong>{{ entry.field }}</strong>
                <p><b>Before:</b> {{ entry.previousValue }}</p>
                <p><b>After:</b> {{ entry.newValue }}</p>
                <small>{{ entry.changedAt }}</small>
              </article>
            } @empty {
              <section class="empty-tab">
                <h2>Change Log</h2>
                <p>Reviewer changes will appear here.</p>
              </section>
            }
          </section>
        }
        </div>
      </aside>
    </section>

    <footer class="review-footer">
      <div class="footer-bulk-actions">
        <button class="accept-high" type="button" [disabled]="!canBatchAcceptHigh" (click)="batchAcceptHigh()">Accept High Confidence</button>
        <button class="accept-all" type="button" [disabled]="!canBatchAcceptPending" (click)="batchAcceptPending()">Accept All Pending</button>
      </div>
      <div class="footer-submit-actions">
        <button class="footer-cancel" type="button" (click)="requestCancelReset()">Cancel</button>
        @if (activeDriverIndex < driverSummaries.length - 1) {
          <button class="footer-submit" type="button" (click)="selectDriver(activeDriverIndex + 1)">Next</button>
        } @else if (submittedToFalcon) {
          <button class="footer-submit" type="button" disabled>Submitted</button>
        } @else if (submitStatus === 'submitting') {
          <button class="footer-submit" type="button" disabled>Submitting...</button>
        } @else {
          <button class="footer-submit" type="button" (click)="openSubmitModal()">Confirm & Submit</button>
        }
      </div>
    </footer>
  </section>

  @if (showCancelModal) {
    <section class="submit-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="cancel-title">
      <article class="submit-modal cancel-modal">
        <h2 id="cancel-title">Cancel Review?</h2>
        <p class="submit-copy">
          Are you sure you want to cancel this MVR review? The current PDF, extracted driver data, and review changes
          will be cleared.
        </p>

        <div class="submit-warning cancel-warning">
          <strong>!</strong>
          <span>This will reset the app back to the upload screen.</span>
        </div>

        <footer class="submit-modal-actions">
          <button class="footer-cancel" type="button" (click)="closeCancelModal()">Keep Reviewing</button>
          <button class="footer-submit danger-submit" type="button" (click)="confirmCancelReset()">Yes, Reset Page</button>
        </footer>
      </article>
    </section>
  }

  @if (showSubmitModal) {
    <section class="submit-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="submit-title">
      <article class="submit-modal">
        <h2 id="submit-title">Confirm Submission</h2>
        <p class="submit-copy">
          Review the summary below. Once submitted, the data will be passed back to Falcon for field population.
        </p>

        <dl class="submit-summary">
          <div>
            <dt>Drivers in document</dt>
            <dd>{{ driverSummaries.length }}</dd>
          </div>
          <div>
            <dt>Fields reviewed</dt>
            <dd>{{ totalFields }}</dd>
          </div>
          <div class="accepted-row">
            <dt>Accepted</dt>
            <dd>{{ acceptedCount }}</dd>
          </div>
          <div class="edited-row">
            <dt>Edited</dt>
            <dd>{{ editedCount }}</dd>
          </div>
          <div class="edited-row">
            <dt>Saved box changes</dt>
            <dd>{{ savedBboxAdjustmentCount }}</dd>
          </div>
          <div class="rejected-row">
            <dt>Rejected</dt>
            <dd>{{ rejectedCount }}</dd>
          </div>
          <div class="pending-row">
            <dt>Pending (unreviewed)</dt>
            <dd>{{ lowPendingFields.length }}</dd>
          </div>
        </dl>

        @if (lowPendingFields.length) {
          <div class="submit-warning">
            <strong>!</strong>
            <span>{{ lowPendingFields.length }} low confidence fields still pending review. You can continue reviewing.</span>
          </div>
        }

        @if (submittedToFalcon) {
          <div class="submit-success">
            <strong>Submitted to Falcon.</strong>
            <span>{{ submitMessage }}</span>
          </div>
        }

        @if (submitStatus === 'submitting') {
          <div class="submit-warning">
            <strong>!</strong>
            <span>{{ submitMessage }}</span>
          </div>
        }

        @if (submitStatus === 'error') {
          <div class="submit-warning">
            <strong>!</strong>
            <span>{{ submitMessage }}</span>
          </div>
        }

        <footer class="submit-modal-actions">
          <button class="footer-cancel" type="button" (click)="closeSubmitModal()">Back to Review</button>
          <button class="footer-submit" type="button" [disabled]="submitStatus === 'submitting'" (click)="submitToFalcon()">
            {{ submitStatus === 'submitting' ? 'Submitting...' : 'Submit to Falcon' }}
          </button>
        </footer>
      </article>
    </section>
  }
  }

  @if (showDuplicateModal) {
    <section class="submit-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="duplicate-title">
      <article class="submit-modal duplicate-modal">
        <header class="duplicate-modal-header">
          <h2 id="duplicate-title">Reprocess Drivers</h2>
          <p class="duplicate-selection-summary" aria-live="polite">
            {{ selectedDuplicateDriverIndexes.size }} of {{ duplicateNoticesForReview.length }} selected
          </p>
        </header>

        <div class="duplicate-driver-toolbar">
          <input
            class="duplicate-driver-search"
            type="search"
            aria-label="Search drivers"
            placeholder="Search name, license, or date of birth"
            [value]="duplicateDriverSearch"
            (input)="updateDuplicateDriverSearch($event)"
          />
        </div>

        <div class="duplicate-driver-list">
          <table class="duplicate-driver-table">
            <thead>
              <tr>
                <th scope="col">
                  <label class="duplicate-select-all">
                    <input
                      type="checkbox"
                      aria-label="Select all drivers"
                      [checked]="allDuplicateDriversSelected"
                      [indeterminate]="someDuplicateDriversSelected"
                      (change)="toggleAllDuplicateDrivers($event)"
                    />
                    <span>Select all</span>
                  </label>
                </th>
                <th scope="col">Driver</th>
                <th scope="col">License</th>
                <th scope="col">Date of birth</th>
              </tr>
            </thead>
            <tbody>
              @for (item of filteredDuplicateNoticesForReview; track item.index) {
                <tr>
                  <td>
                    <input
                      type="checkbox"
                      [attr.aria-label]="'Select ' + (item.notice.driverName || item.notice.message)"
                      [checked]="selectedDuplicateDriverIndexes.has(item.index)"
                      (change)="toggleDuplicateDriver(item.index, $event)"
                    />
                  </td>
                  <td class="duplicate-driver-name">{{ item.notice.driverName || item.notice.message }}</td>
                  <td>{{ item.notice.licenseNumber || '--' }}</td>
                  <td>{{ item.notice.dob || '--' }}</td>
                </tr>
              } @empty {
                <tr>
                  <td class="duplicate-driver-empty" colspan="4">No drivers match your search.</td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <footer class="submit-modal-actions duplicate-modal-actions">
          <p class="duplicate-driver-warning">Reprocessing may overwrite existing results for the selected drivers.</p>
          <div class="duplicate-action-buttons">
            <button class="footer-cancel" type="button" (click)="cancelDuplicateProcessing()">Cancel</button>
            <button
              class="footer-submit"
              type="button"
              [disabled]="selectedDuplicateDriverIndexes.size === 0"
              (click)="processDuplicateDriverAgain()"
            >
              Process selected ({{ selectedDuplicateDriverIndexes.size }})
            </button>
          </div>
        </footer>
      </article>
    </section>
  }
</main>

